import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PackageOpen, TrendingUp, Loader2 } from "lucide-react";

const BG =
  "https://images.unsplash.com/photo-1672552226650-796f40198c47?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxkYXJrJTIwd2FyZWhvdXNlJTIwbG9naXN0aWNzJTIwZ2VvbWV0cmljfGVufDB8fHx8MTc4NTI3MzE2OHww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name || "Seller");
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = () => {
    setMode("login");
    setEmail("admin@fbadash.com");
    setPassword("admin123");
  };

  return (
    <div className="min-h-screen flex">
      {/* Left visual */}
      <div className="hidden lg:flex relative w-1/2 overflow-hidden">
        <img src={BG} alt="warehouse" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-2 text-white">
            <PackageOpen className="h-7 w-7 text-[#007AFF]" />
            <span className="font-head font-extrabold text-xl tracking-tight">FBAStar</span>
          </div>
          <div>
            <h1 className="font-head text-4xl xl:text-5xl font-black tracking-tighter text-white leading-tight">
              Turn Amazon reports into <span className="text-[#007AFF]">profit clarity</span>.
            </h1>
            <p className="mt-5 text-[#A3A3A3] text-base max-w-md">
              Sales analytics, cost & profit tracking, and FBA inventory control — all from your
              own report exports. Multi-currency across US, UK & EU.
            </p>
            <div className="mt-8 flex items-center gap-2 text-[#10B981] text-sm font-semibold">
              <TrendingUp className="h-4 w-4" /> Built for data-driven sellers
            </div>
          </div>
          <div className="text-xs text-[#666]">© {new Date().getFullYear()} FBAStar Analytics</div>
        </div>
      </div>

      {/* Right form */}
      <div className="app-bg flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md fade-up">
          <div className="lg:hidden flex items-center gap-2 mb-8 text-white">
            <PackageOpen className="h-7 w-7 text-[#007AFF]" />
            <span className="font-head font-extrabold text-xl">FBAStar</span>
          </div>
          <h2 className="font-head text-3xl font-bold tracking-tight text-white">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h2>
          <p className="mt-2 text-sm text-[#A3A3A3]">
            {mode === "login"
              ? "Sign in to access your seller dashboard."
              : "Start analyzing your Amazon business in minutes."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-xs uppercase tracking-widest text-[#A3A3A3]">Name</Label>
                <Input
                  id="name"
                  data-testid="register-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Seller"
                  className="bg-[#111] border-[#262626] h-11 text-white focus-visible:ring-[#007AFF]"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-[#A3A3A3]">Email</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@store.com"
                className="bg-[#111] border-[#262626] h-11 text-white focus-visible:ring-[#007AFF]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-[#A3A3A3]">Password</Label>
              <Input
                id="password"
                type="password"
                required
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-[#111] border-[#262626] h-11 text-white focus-visible:ring-[#007AFF]"
              />
            </div>

            {error && (
              <div data-testid="auth-error" className="text-sm text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="auth-submit-button"
              disabled={loading}
              className="w-full h-11 bg-[#007AFF] hover:bg-[#005BB5] text-white font-semibold rounded-full transition-colors duration-200"
            >
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <div className="mt-6 flex items-center justify-between text-sm">
            <button
              data-testid="toggle-auth-mode"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-[#A3A3A3] hover:text-white transition-colors duration-200"
            >
              {mode === "login" ? "Need an account? Sign up" : "Have an account? Sign in"}
            </button>
            <button
              data-testid="demo-fill-button"
              onClick={fillDemo}
              className="text-[#007AFF] hover:text-[#4da3ff] transition-colors duration-200 font-semibold"
            >
              Use demo login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
